import { useState } from "react";
import limeWok from "@/assets/gallery/24_-_Lime_wok.jpg";
import burgerHeaven from "@/assets/gallery/45_-_Burger_Heaven.jpg";
import achariHandi from "@/assets/gallery/121_-_Achari_Handi.jpg";
import naan from "@/assets/gallery/Naan.jpg";
import sushi from "@/assets/gallery/Sushi.jpg";
import biryani from "@/assets/gallery/67_-_Biryani.jpg";
import garlicChicken from "@/assets/gallery/25_-_Garlic_Chicken_Tikka_Sizzler.jpg";
import samosa from "@/assets/gallery/2_-_Vegetable_Samosa.jpg";
import ImageLightbox from "@/components/ImageLightbox";
import GalleryCard from "@/components/GalleryCard";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

const Gallery = () => {
  const { t } = useLanguage();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [filter, setFilter] = useState<string>("all");
  const [videoReady, setVideoReady] = useState(false);

  const images = [
    { src: limeWok, alt: "Lime wok", title: "Lime wok", number: "24", category: "thai" },
    { src: burgerHeaven, alt: "Burger Heaven", title: "Burger Heaven", number: "45", category: "western" },
    { src: achariHandi, alt: "Achari Handi", title: "Achari Handi", number: "121", category: "indian" },
    { src: naan, alt: "Naan", title: "Naan", number: "", category: "indian" },
    { src: sushi, alt: "Sushi", title: "Sushi", number: "", category: "sushi" },
    { src: biryani, alt: "Biryani", title: "Biryani", number: "67", category: "indian" },
    { src: garlicChicken, alt: "Garlic Chicken Tikka Sizzler", title: "Garlic Chicken Tikka Sizzler", number: "25", category: "indian" },
    { src: samosa, alt: "Vegetable Samosa", title: "Vegetable Samosa", number: "2", category: "indian" },
  ];

  const filteredImages = filter === "all" 
    ? images 
    : images.filter(img => img.category === filter);

  const firstRowImages = filteredImages.slice(0, 4);
  const secondRowImages = filteredImages.slice(4, 8);

  const filterButtons = [
    { label: t('gallery.all'), value: "all" },
    { label: t('gallery.indian'), value: "indian" },
    { label: t('gallery.thai'), value: "thai" },
    { label: t('gallery.sushi'), value: "sushi" },
    { label: t('gallery.other'), value: "western" },
  ];

  return (
    <section id="gallery" className="section-padding bg-card">
      <div className="container-custom">
        <div className="text-center mb-16">
          <p className="text-gold text-lg font-medium tracking-wider uppercase mb-4 animate-text-glow">{t('gallery.title')}</p>
          <h2 className="heading-luxury text-foreground mb-6 text-gradient">
            {t('gallery.heading')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            {t('gallery.description')}
          </p>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {filterButtons.map((btn) => (
              <Button
                key={btn.value}
                onClick={() => setFilter(btn.value)}
                variant={filter === btn.value ? "default" : "outline"}
                className={`${
                  filter === btn.value
                    ? "bg-gold text-background hover:bg-gold-light"
                    : "border-2 border-gold text-gold hover:bg-gold hover:text-background"
                } transition-all duration-300 transform hover:scale-105`}
              >
                {btn.label}
              </Button>
            ))}
          </div>
        </div>

        {/* First row - 4 images (eager loading) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-4 sm:mb-6">
          {firstRowImages.map((image, index) => (
            <GalleryCard
              key={index}
              image={image}
              onClick={() => setSelectedImageIndex(filteredImages.indexOf(image))}
            />
          ))}
        </div>

        {/* Video row - autoplay, no lazy loading, inline src for instant load */}
        <div className="mb-4 sm:mb-6 rounded-xl overflow-hidden bg-black relative">
          {/* Loading animation overlay */}
          {/* Advanced loading overlay */}
          <div
            className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
            style={{
              opacity: videoReady ? 0 : 1,
              transition: 'opacity 800ms ease-out',
            }}
          >
            {/* Radial gold glow */}
            <div className="absolute inset-0 animate-[hero-glow-pulse_3s_ease-in-out_infinite]"
              style={{ background: 'radial-gradient(circle at center, hsl(var(--gold) / 0.15) 0%, transparent 70%)' }}
            />

            {/* Concentric pulsing rings */}
            {[80, 120, 160].map((size, i) => (
              <div
                key={i}
                className="absolute rounded-full border animate-[hero-ring-pulse_3s_ease-in-out_infinite]"
                style={{
                  width: `${size}px`,
                  height: `${size}px`,
                  borderColor: `hsl(var(--gold) / ${0.4 - i * 0.1})`,
                  animationDelay: `${i * 0.4}s`,
                }}
              />
            ))}

            {/* Floating gold particles */}
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={`p-${i}`}
                className="absolute w-1 h-1 rounded-full animate-[hero-particle-float_4s_ease-in-out_infinite]"
                style={{
                  backgroundColor: `hsl(var(--gold) / ${0.4 + Math.random() * 0.4})`,
                  left: `${15 + (i * 10)}%`,
                  top: `${20 + (i % 3) * 25}%`,
                  animationDelay: `${i * 0.5}s`,
                  animationDuration: `${3 + (i % 3)}s`,
                }}
              />
            ))}

            {/* Shimmer text */}
            <div className="flex items-center gap-4 z-10">
              <span className="text-gold font-light tracking-[0.3em] uppercase text-sm animate-[hero-shimmer_2.5s_ease-in-out_infinite]"
                style={{ backgroundImage: 'linear-gradient(90deg, hsl(var(--gold)), hsl(var(--gold-light)), hsl(var(--gold)))', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundSize: '200% 100%' }}
              >Upplev</span>
              <div className="w-2 h-2 rounded-full bg-gold/60 animate-pulse" />
              <span className="text-gold font-light tracking-[0.3em] uppercase text-sm animate-[hero-shimmer_2.5s_ease-in-out_infinite]"
                style={{ backgroundImage: 'linear-gradient(90deg, hsl(var(--gold)), hsl(var(--gold-light)), hsl(var(--gold)))', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundSize: '200% 100%', animationDelay: '0.3s' }}
              >atmosfären</span>
            </div>
          </div>
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onCanPlay={() => setVideoReady(true)}
            onError={(e) => {
              // Suppress console error for video load failures
              e.currentTarget.style.display = 'none';
            }}
            className="w-full max-h-[500px] object-cover transition-opacity duration-700"
            style={{ opacity: videoReady ? 1 : 0 }}
          >
            <source src="/videos/gallery-video.mp4" type="video/mp4" />
          </video>
        </div>

        {/* Second row - 4 images (eager loading - no lazy) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {secondRowImages.map((image, index) => (
            <GalleryCard
              key={index + 4}
              image={image}
              onClick={() => setSelectedImageIndex(filteredImages.indexOf(image))}
            />
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-muted-foreground text-lg">
            {t('gallery.social')}
          </p>
        </div>
      </div>

      {/* Lightbox */}
      {selectedImageIndex !== null && (
        <ImageLightbox
          images={filteredImages}
          currentIndex={selectedImageIndex}
          onClose={() => setSelectedImageIndex(null)}
        />
      )}
    </section>
  );
};

export default Gallery;